/**
 * RunnersPanel — build workery: registrované (catalog, GitLab i GitHub)
 * i ACM docker gitlab-runner.
 *
 * Záložka dřív četla jen acm.gitlab_runners — registrovaný worker
 * (gitlab.worker_set, např. apps-build) se vůbec neukázal.
 *
 * Widget-first: obsah bez vnějšího Card (chrome kreslí WidgetShell).
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Play, Plus, RotateCcw, Square, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  EmptyState,
  Input,
  Modal,
  StatusDot,
} from '../../../components/ui';
import { Select } from '../../../components/ui/Select';
import { callWidgetTool } from '../../../widgets/scope';
import type { EffectiveScope, WidgetRenderProps } from '../../../widgets/types';
import { themedConfirm } from '../../../lib/themed-dialogs';

type WorkerPlatform = 'gitlab' | 'github';
type WorkerSource = 'registered' | 'managed' | 'both';

interface Worker {
  id: string;
  source: WorkerSource;
  platform: WorkerPlatform;
  host: string | null;
  tag: string | null;
  fleet: string | null;
  concurrent: number | null;
  connection_id: string | null;
  status: string | null;
  gitlab_url: string | null;
  instance_name: string | null;
  executor: string | null;
  docker_image: string | null;
  node: string | null;
  container: string | null;
}
interface WorkersResp {
  ok: boolean;
  count?: number;
  workers: Worker[];
}
interface Instance {
  name: string;
  external_url: string | null;
  status: string;
}
interface InstancesResp {
  ok: boolean;
  instances: Instance[];
}
interface ServersResp {
  ok: boolean;
  servers: Array<{
    id: string;
    host: string;
    has_token: boolean;
    type?: 'gitlab' | 'github';
    kind?: string;
  }>;
}
interface Peer {
  name: string;
  label?: string;
  host?: string;
}
interface PeersResp {
  ok?: boolean;
  nodes?: Peer[];
}

function statusToDot(status: string | null): 'running' | 'stopped' | 'error' | 'pending' {
  if (status === 'running') return 'running';
  if (status === 'error') return 'error';
  if (status === 'registered' || status === 'deleting' || status === 'created') return 'pending';
  return 'stopped';
}

function nodeKey(scope: EffectiveScope): string {
  return scope.node ?? 'local';
}

export function RunnersPanel({ scope }: WidgetRenderProps = {} as WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [newOpen, setNewOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Worker | null>(null);
  const sc: EffectiveScope = scope ?? { node: null, identity: null };
  const nk = nodeKey(sc);

  const q = useQuery<WorkersResp>({
    queryKey: ['gitlab-workers', nk],
    queryFn: () => callWidgetTool<WorkersResp>('gitlab.workers_list', {}, sc, { silent: true }),
    staleTime: 15_000,
  });

  const workers = q.data?.workers ?? [];

  const manageMut = useMutation({
    mutationFn: (args: { name: string; action: 'start' | 'stop' | 'restart' }) =>
      callWidgetTool<{ ok?: boolean; error?: string }>('gitlab.runner_manage', args, sc, {
        label: t('gitlab.runners.actionLabel', { action: args.action, name: args.name }),
      }),
    onSuccess: (r) => {
      if (r?.ok === false) return;
      qc.invalidateQueries({ queryKey: ['gitlab-workers', nk] });
    },
  });

  return (
    <div className="p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-zinc-400">
          {t('gitlab.runners.title', { count: workers.length })}
        </span>
        <Button variant="primary" size="sm" onClick={() => setNewOpen(true)}>
          <Plus size={13} /> {t('gitlab.runners.newBtn')}
        </Button>
      </div>

      {q.isLoading
        ? <div className="text-sm text-zinc-500">{t('common.loading')}</div>
        : workers.length === 0
        ? (
          <EmptyState
            title={t('gitlab.runners.emptyTitle')}
            description={t('gitlab.runners.emptyDesc')}
          />
        )
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-zinc-800 text-xs text-zinc-400">
                  <th className="text-left py-2 pr-3">{t('gitlab.runners.colName')}</th>
                  <th className="text-left py-2 pr-3">{t('gitlab.runners.colPlatform')}</th>
                  <th className="text-left py-2 pr-3">{t('gitlab.runners.colGitlab')}</th>
                  <th className="text-left py-2 pr-3">{t('gitlab.runners.colNode')}</th>
                  <th className="text-left py-2 pr-3">{t('gitlab.runners.colTags')}</th>
                  <th className="text-right py-2 pr-3">{t('gitlab.runners.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {workers.map((r) => (
                  <WorkerRow
                    key={r.id}
                    worker={r}
                    onManage={(action) => manageMut.mutate({ name: r.id, action })}
                    onDelete={() => setDeleteTarget(r)}
                    pending={manageMut.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

      {newOpen && (
        <NewWorkerModal
          scope={sc}
          onClose={() => setNewOpen(false)}
          onCreated={() => {
            setNewOpen(false);
            qc.invalidateQueries({ queryKey: ['gitlab-workers', nk] });
          }}
        />
      )}

      {deleteTarget && (
        <DeleteWorkerModal
          scope={sc}
          worker={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            qc.invalidateQueries({ queryKey: ['gitlab-workers', nk] });
          }}
        />
      )}
    </div>
  );
}

function WorkerRow({ worker, onManage, onDelete, pending }: {
  worker: Worker;
  onManage: (a: 'start' | 'stop' | 'restart') => void;
  onDelete: () => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const running = worker.status === 'running';
  const managed = worker.source === 'managed' || worker.source === 'both';
  const gitlabLabel = worker.instance_name
    ? `ACM: ${worker.instance_name}`
    : worker.gitlab_url
    ? worker.gitlab_url.replace(/^https?:\/\//, '').replace(/\/+$/, '')
    : worker.connection_id
    ? worker.connection_id
    : worker.host ?? '—';
  const where = worker.node ?? worker.host ?? '—';
  return (
    <tr className="border-b border-zinc-900 hover:bg-zinc-900/30">
      <td className="py-2 pr-3">
        <div className="font-medium flex items-center gap-2">
          <StatusDot status={statusToDot(worker.status)} />
          {worker.id}
        </div>
        <div className="text-xs text-zinc-500">
          {worker.source === 'registered'
            ? t('gitlab.runners.sourceRegistered')
            : worker.source === 'both'
            ? t('gitlab.runners.sourceBoth')
            : t('gitlab.runners.sourceManaged')}
          {worker.executor ? ` · ${worker.executor}` : ''}
          {worker.docker_image ? ` · ${worker.docker_image}` : ''}
          {worker.fleet ? ` · ${worker.fleet}` : ''}
        </div>
      </td>
      <td className="py-2 pr-3">
        <Badge variant={worker.platform === 'github' ? 'purple' : 'blue'}>
          {worker.platform === 'github' ? 'GitHub' : 'GitLab'}
        </Badge>
      </td>
      <td className="py-2 pr-3">
        <Badge variant={worker.instance_name ? 'blue' : 'gray'}>{gitlabLabel}</Badge>
      </td>
      <td className="py-2 pr-3">
        <Badge variant="purple">{where}</Badge>
      </td>
      <td className="py-2 pr-3 text-xs text-zinc-400">
        {worker.tag ?? <span className="text-zinc-600">—</span>}
      </td>
      <td className="py-2 pr-3">
        <div className="flex items-center justify-end gap-1">
          {managed && (
            <>
              <Button
                variant="ghost"
                size="sm"
                disabled={pending || running}
                onClick={() => onManage('start')}
                title={t('gitlab.runners.start')}
              >
                <Play size={13} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={pending || !running}
                onClick={() => onManage('stop')}
                title={t('gitlab.runners.stop')}
              >
                <Square size={13} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={pending}
                onClick={() => onManage('restart')}
                title={t('gitlab.runners.restart')}
              >
                <RotateCcw size={13} />
              </Button>
            </>
          )}
          <Button
            variant="danger"
            size="sm"
            disabled={pending}
            onClick={onDelete}
            title={t('gitlab.runners.delete')}
          >
            <Trash2 size={13} />
          </Button>
        </div>
      </td>
    </tr>
  );
}

type NewMode = 'register' | 'provision';
type GitlabTargetKind = 'instance' | 'external' | 'url';

function NewWorkerModal({ scope, onClose, onCreated }: {
  scope: EffectiveScope;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<NewMode>('register');

  const instancesQ = useQuery<InstancesResp>({
    queryKey: ['gitlab-instances', nodeKey(scope)],
    queryFn: () => callWidgetTool<InstancesResp>('gitlab.instances_list', {}, scope, { silent: true }),
    staleTime: 30_000,
  });
  const serversQ = useQuery<ServersResp>({
    queryKey: ['gitlab-connections', nodeKey(scope)],
    queryFn: () => callWidgetTool<ServersResp>('gitlab.connections_list', {}, scope, { silent: true }),
    staleTime: 30_000,
  });
  const peersQ = useQuery<PeersResp>({
    queryKey: ['mesh-peers', nodeKey(scope)],
    queryFn: () => callWidgetTool<PeersResp>('mesh.peers_list', {}, scope, { silent: true }),
    staleTime: 60_000,
  });

  const instances = instancesQ.data?.instances ?? [];
  const servers = serversQ.data?.servers ?? [];
  const peers = peersQ.data?.nodes ?? [];

  const [platform, setPlatform] = useState<WorkerPlatform>('gitlab');
  const [id, setId] = useState('');
  const [host, setHost] = useState('');
  const [tag, setTag] = useState('');
  const [fleet, setFleet] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [token, setToken] = useState('');

  const [kind, setKind] = useState<GitlabTargetKind>('instance');
  const [instanceName, setInstanceName] = useState('');
  const [externalId, setExternalId] = useState('');
  const [directUrl, setDirectUrl] = useState('');
  const [name, setName] = useState('');
  const [tokenKind, setTokenKind] = useState<'registration' | 'auth'>('registration');
  const [tags, setTags] = useState('');
  const [dockerImage, setDockerImage] = useState('alpine:latest');
  const [targetNode, setTargetNode] = useState('');
  const [runUntagged, setRunUntagged] = useState(false);

  const platformServers = servers.filter((s) =>
    platform === 'github' ? s.type === 'github' : s.type !== 'github'
  );

  const gitlab = useMemo(() => {
    if (kind === 'instance') {
      const inst = instances.find((i) => i.name === instanceName);
      return { url: inst?.external_url ?? '', instance_name: instanceName };
    }
    if (kind === 'external') {
      const srv = servers.find((s) => s.id === externalId);
      return { url: srv?.host ?? '', instance_name: null };
    }
    return { url: directUrl.trim(), instance_name: null };
  }, [kind, instanceName, externalId, directUrl, instances, servers]);

  const registerMut = useMutation({
    mutationFn: () =>
      callWidgetTool<{ ok?: boolean; error?: string; id?: string }>(
        'gitlab.worker_set',
        {
          id,
          host,
          tag,
          platform,
          ...(fleet ? { fleet } : {}),
          ...(connectionId ? { connection: connectionId } : {}),
          ...(token ? { registration_token: token } : {}),
        },
        scope,
        { label: t('gitlab.runners.registerLabel', { name: id }) },
      ),
    onSuccess: (r) => {
      if (r?.ok === false) {
        toast.error(r.error ?? t('gitlab.runners.registerFailed'));
        return;
      }
      toast.success(t('gitlab.runners.registeredOk', { name: r?.id ?? id }));
      onCreated();
    },
  });

  const provisionMut = useMutation({
    mutationFn: () =>
      callWidgetTool<{ ok?: boolean; error?: string; name?: string; status?: string }>(
        'gitlab.runner_provision',
        {
          name,
          token,
          token_kind: tokenKind,
          ...(gitlab.instance_name ? { instance_name: gitlab.instance_name } : {}),
          ...(gitlab.url && !gitlab.instance_name ? { gitlab_url: gitlab.url } : {}),
          ...(tags ? { tags } : {}),
          ...(dockerImage ? { docker_image: dockerImage } : {}),
          ...(targetNode ? { target_node: targetNode } : {}),
          run_untagged: runUntagged,
          start: true,
        },
        scope,
        { label: t('gitlab.runners.provisionLabel', { name }) },
      ),
    onSuccess: (r) => {
      if (r?.ok === false) return;
      toast.success(t('gitlab.runners.createdOk', { name: r?.name ?? name }));
      onCreated();
    },
  });

  const registerValid = id.trim().length > 0 && host.trim().length > 0 && tag.trim().length > 0;
  const provisionValid =
    name.trim().length > 0 &&
    token.trim().length > 0 &&
    ((kind === 'instance' && !!instanceName) ||
      (kind === 'external' && !!externalId) ||
      (kind === 'url' && !!directUrl.trim()));

  const pending = registerMut.isPending || provisionMut.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={t('gitlab.runners.newTitle')}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          {mode === 'register'
            ? (
              <Button
                variant="primary"
                loading={pending}
                disabled={!registerValid}
                onClick={() => registerMut.mutate()}
              >
                {t('gitlab.runners.registerBtn')}
              </Button>
            )
            : (
              <Button
                variant="primary"
                loading={pending}
                disabled={!provisionValid}
                onClick={() => provisionMut.mutate()}
              >
                {t('gitlab.runners.provisionBtn')}
              </Button>
            )}
        </div>
      }
    >
      <div className="space-y-3">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMode('register')}
            className={`text-xs px-2 py-1 rounded border ${
              mode === 'register'
                ? 'bg-sky-500/20 border-sky-500 text-sky-300'
                : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
            }`}
          >
            {t('gitlab.runners.modeRegister')}
          </button>
          <button
            type="button"
            onClick={() => setMode('provision')}
            className={`text-xs px-2 py-1 rounded border ${
              mode === 'provision'
                ? 'bg-sky-500/20 border-sky-500 text-sky-300'
                : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
            }`}
          >
            {t('gitlab.runners.modeProvision')}
          </button>
        </div>

        {mode === 'register' && (
          <>
            <p className="text-xs text-zinc-500">{t('gitlab.runners.registerHelp')}</p>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">
                {t('gitlab.runners.fieldPlatform')}
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setPlatform('gitlab'); setConnectionId(''); }}
                  className={`text-xs px-2 py-1 rounded border ${
                    platform === 'gitlab'
                      ? 'bg-sky-500/20 border-sky-500 text-sky-300'
                      : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
                  }`}
                >
                  GitLab
                </button>
                <button
                  type="button"
                  onClick={() => { setPlatform('github'); setConnectionId(''); }}
                  className={`text-xs px-2 py-1 rounded border ${
                    platform === 'github'
                      ? 'bg-purple-500/20 border-purple-500 text-purple-300'
                      : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
                  }`}
                >
                  GitHub
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">
                {t('gitlab.runners.fieldName')}
              </label>
              <Input
                placeholder="apps-build"
                value={id}
                onChange={(e) => setId(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">
                {t('gitlab.runners.fieldHost')}
              </label>
              <Input
                placeholder="10.119.251.19"
                value={host}
                onChange={(e) => setHost(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">
                {platform === 'github'
                  ? t('gitlab.runners.fieldLabels')
                  : t('gitlab.runners.fieldTags')}
              </label>
              <Input
                placeholder={platform === 'github' ? 'self-hosted,linux' : 'apps-build'}
                value={tag}
                onChange={(e) => setTag(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">
                {t('gitlab.runners.fieldFleet')}
              </label>
              <Input
                placeholder="acm-dev"
                value={fleet}
                onChange={(e) => setFleet(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">
                {t('gitlab.runners.fieldConnection')}
              </label>
              <Select value={connectionId} onChange={(e) => setConnectionId(e.target.value)}>
                <option value="">{t('gitlab.runners.connectionOptional')}</option>
                {platformServers.map((s) => (
                  <option key={s.id} value={s.id}>{s.id} ({s.host})</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">
                {t('gitlab.runners.fieldTokenOptional')}
              </label>
              <Input
                type="password"
                placeholder={platform === 'github'
                  ? t('gitlab.runners.tokenPlaceholderGithub')
                  : t('gitlab.runners.tokenPlaceholder')}
                value={token}
                onChange={(e) => setToken(e.target.value)}
              />
            </div>
          </>
        )}

        {mode === 'provision' && (
          <>
            <p className="text-xs text-zinc-500">{t('gitlab.runners.provisionHelp')}</p>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">
                {t('gitlab.runners.fieldName')}
              </label>
              <Input
                placeholder="build-worker-1"
                value={name}
                onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">
                {t('gitlab.runners.fieldGitlab')}
              </label>
              <div className="flex gap-2 mb-1">
                <button
                  type="button"
                  onClick={() => setKind('instance')}
                  className={`text-xs px-2 py-1 rounded border ${
                    kind === 'instance'
                      ? 'bg-sky-500/20 border-sky-500 text-sky-300'
                      : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
                  }`}
                >
                  {t('gitlab.runners.gitlabKindInstance')}
                </button>
                <button
                  type="button"
                  onClick={() => setKind('external')}
                  className={`text-xs px-2 py-1 rounded border ${
                    kind === 'external'
                      ? 'bg-sky-500/20 border-sky-500 text-sky-300'
                      : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
                  }`}
                >
                  {t('gitlab.runners.gitlabKindExternal')}
                </button>
                <button
                  type="button"
                  onClick={() => setKind('url')}
                  className={`text-xs px-2 py-1 rounded border ${
                    kind === 'url'
                      ? 'bg-sky-500/20 border-sky-500 text-sky-300'
                      : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
                  }`}
                >
                  {t('gitlab.runners.gitlabKindUrl')}
                </button>
              </div>
              {kind === 'instance' && (
                <Select value={instanceName} onChange={(e) => setInstanceName(e.target.value)}>
                  <option value="">{t('gitlab.runners.pickInstance')}</option>
                  {instances.map((i) => (
                    <option key={i.name} value={i.name}>
                      {i.name} {i.external_url ? `(${i.external_url.replace(/^https?:\/\//, '')})` : ''}
                    </option>
                  ))}
                </Select>
              )}
              {kind === 'external' && (
                <Select value={externalId} onChange={(e) => setExternalId(e.target.value)}>
                  <option value="">{t('gitlab.runners.pickExternal')}</option>
                  {servers.filter((s) => s.type !== 'github').map((s) => (
                    <option key={s.id} value={s.id}>{s.id} ({s.host})</option>
                  ))}
                </Select>
              )}
              {kind === 'url' && (
                <Input
                  placeholder="https://gitlab.example.com"
                  value={directUrl}
                  onChange={(e) => setDirectUrl(e.target.value)}
                />
              )}
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">
                {t('gitlab.runners.fieldToken')}
              </label>
              <Input
                type="password"
                placeholder={t('gitlab.runners.tokenPlaceholder')}
                value={token}
                onChange={(e) => setToken(e.target.value)}
              />
              <div className="flex gap-2 mt-1 text-[10px] text-zinc-500">
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    name="tokenkind"
                    checked={tokenKind === 'registration'}
                    onChange={() => setTokenKind('registration')}
                  /> registration (GitLab &lt; 16)
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    name="tokenkind"
                    checked={tokenKind === 'auth'}
                    onChange={() => setTokenKind('auth')}
                  /> auth (GitLab 16+)
                </label>
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">
                {t('gitlab.runners.fieldTargetNode')}
              </label>
              <Select value={targetNode} onChange={(e) => setTargetNode(e.target.value)}>
                <option value="">{t('gitlab.runners.nodeLocal')}</option>
                {peers.map((p) => (
                  <option key={p.name} value={p.name}>
                    {p.label ?? p.name} {p.host ? `(${p.host})` : ''}
                  </option>
                ))}
              </Select>
              <p className="text-[10px] text-zinc-500 mt-0.5">
                {t('gitlab.runners.targetNodeHelp')}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-zinc-400 block mb-1">
                  {t('gitlab.runners.fieldTags')}
                </label>
                <Input
                  placeholder="compile,docker"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400 block mb-1">
                  {t('gitlab.runners.fieldImage')}
                </label>
                <Input
                  placeholder="alpine:latest"
                  value={dockerImage}
                  onChange={(e) => setDockerImage(e.target.value)}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={runUntagged}
                onChange={(e) => setRunUntagged(e.target.checked)}
                className="accent-sky-500"
              />
              {t('gitlab.runners.runUntagged')}
            </label>
          </>
        )}
      </div>
    </Modal>
  );
}

interface DeleteResp {
  ok?: boolean;
  name?: string;
  id?: string;
  locked?: boolean;
  backup_failed?: boolean;
  error?: string;
  errors?: string[];
}

function DeleteWorkerModal({ scope, worker, onClose, onDeleted }: {
  scope: EffectiveScope;
  worker: Worker;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const [pin, setPin] = useState('');
  const [showedLocked, setShowedLocked] = useState(false);
  const managed = worker.source === 'managed' || worker.source === 'both';
  const registered = worker.source === 'registered' || worker.source === 'both';

  const mut = useMutation<DeleteResp>({
    mutationFn: async () => {
      if (registered) {
        const unreg = await callWidgetTool<DeleteResp>(
          'gitlab.worker_delete',
          { id: worker.id },
          scope,
          { label: t('gitlab.runners.unregisterLabel', { name: worker.id }) },
        );
        if (unreg?.ok === false) return unreg;
      }
      if (managed) {
        return callWidgetTool<DeleteResp>(
          'gitlab.runner_delete',
          {
            name: worker.id,
            ...(pin ? { pin } : {}),
            force_no_backup: true,
          },
          scope,
          { label: t('gitlab.runners.deleteLabel', { name: worker.id }) },
        );
      }
      return { ok: true, id: worker.id };
    },
    onSuccess: (r) => {
      if (r?.locked) {
        setShowedLocked(true);
        return;
      }
      if (r?.ok === false) return;
      toast.success(t('gitlab.runners.deletedOk', { name: worker.id }));
      onDeleted();
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={t('gitlab.runners.deleteTitle', { name: worker.id })}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="danger"
            loading={mut.isPending}
            disabled={showedLocked && !pin}
            onClick={async () => {
              if (registered && !managed) {
                if (!(await themedConfirm(t('gitlab.runners.confirmUnregister', { name: worker.id })))) {
                  return;
                }
              }
              mut.mutate();
            }}
          >
            <Trash2 size={13} /> {t('gitlab.runners.deleteConfirm')}
          </Button>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <p>
          {registered && !managed
            ? t('gitlab.runners.unregisterInfo', { name: worker.id })
            : t('gitlab.runners.deleteInfo', { node: worker.node ?? worker.host ?? 'local' })}
        </p>
        {worker.node && worker.node !== 'local' && managed && (
          <div className="rounded border border-yellow-700/30 bg-yellow-950/20 px-3 py-2 text-xs text-yellow-300">
            {t('gitlab.runners.deleteCrossNodeHint', { node: worker.node })}
          </div>
        )}
        {showedLocked && (
          <div className="rounded-md border border-yellow-700/40 bg-yellow-950/30 px-3 py-2">
            <p className="text-sm text-yellow-300 mb-2">
              🔒 {t('gitlab.runners.lockedInfo')}
            </p>
            <Input
              type="password"
              placeholder={t('gitlab.instances.pinPlaceholder')}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              autoFocus
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
